import { useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import { accountSwitchesApi } from '../../axios';
import { useLanguage } from '../../hooks';
import { paths } from '../../utils/navigation';
import { clearAccountScopedStorage } from './accountScopedStorage';

export interface AccountSwitchState {
  /** The account being moved to while the request is in flight, otherwise null. */
  switchingTo: number | null;
  /** Its name, for the transition screen. Empty when no switch is running. */
  switchingToName: string;
  switchAccount: (userId: number, accountName: string) => Promise<void>;
}

/**
 * Moves the session to another linked account, one switch at a time.
 *
 * The guard and the in-flight account both live here, above the user menu, and
 * not inside the selector: the selector renders inside a Popover that unmounts
 * its children on close. Held by the selector, the guard would be torn down
 * while the request is still in flight and reopening the menu would allow a
 * second switch on top of the first. It is a ref rather than state because two
 * clicks in the same tick read the same render.
 *
 * Starting a switch deliberately leaves the menu open. The row spinner is the
 * only feedback the person gets while the POST and the navigation run, which can
 * take seconds, and closing the menu would unmount it: the click would then be
 * indistinguishable from the menu simply closing. Keeping the menu up also puts
 * the error snackbar over the row that failed. The navigation resolves the menu on
 * success, and on failure the person is left looking at the list they chose from.
 *
 * On success the session leaves through a document navigation to the site root,
 * not a reload of the page that was open. Two reasons, and the second is the one
 * QA hit: everything in memory belongs to the previous account, so a full load is
 * simpler and safer than invalidating each cache separately; and the page that
 * was open is not necessarily reachable in the account being entered, where the
 * role can be another one. Reloading the user list as an Operario rendered a
 * blank screen. The target is the root and not the home path so that the app's
 * own default redirect picks the destination for the entering account, which may
 * route to Ask DataScope instead of Inicio.
 */
export function useAccountSwitch(): AccountSwitchState {
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const inFlight = useRef(false);
  const [switchingTo, setSwitchingTo] = useState<number | null>(null);
  // Carried with the switch instead of looked up: resolving the name from the
  // account list would make every page load of every multi-account user fetch
  // that list, for a value only needed once a switch is actually running.
  const [switchingToName, setSwitchingToName] = useState('');

  const switchAccount = async (userId: number, accountName: string) => {
    if (inFlight.current) return;
    inFlight.current = true;

    setSwitchingTo(userId);
    setSwitchingToName(accountName);

    try {
      await accountSwitchesApi.switchAccount(userId);
      // The navigation clears memory but not storage, so whatever is keyed by the
      // previous account's ids has to go before the new one rehydrates it.
      clearAccountScopedStorage();
      window.location.assign(paths.home.index);
    } catch (error) {
      console.error(error);
      // The session is still on the previous account: the server did not switch it.
      inFlight.current = false;
      setSwitchingTo(null);
      setSwitchingToName('');
      enqueueSnackbar(lang.mainTopBar.accountSwitcher.error, { variant: 'error' });
    }
  };

  return { switchingTo, switchingToName, switchAccount };
}
